import { ref } from 'vue';
import { httpService, type HttpError } from '../services/httpService';
import { log } from '../utils/DebugLogger';

// Import side-effect: registers the contracts with the runtime registry.
import '../contracts/Clientes_EnviarNotificacaoInput';
import '../contracts/Clientes_EnviarNotificacaoResponse';
import '../contracts/Clientes_NotificacoesPortalResponse';

import type { Clientes_EnviarNotificacaoInput } from '../contracts/Clientes_EnviarNotificacaoInput';
import type { Clientes_EnviarNotificacaoResponse } from '../contracts/Clientes_EnviarNotificacaoResponse';
import type { Clientes_NotificacoesPortalResponse } from '../contracts/Clientes_NotificacoesPortalResponse';
import type { Clientes_NotificacaoPortalRow } from '../contracts/Clientes_NotificacaoPortalRow';

/**
 * useClienteNotificacao — send one free-text message to a customer's portal inbox.
 *
 * Separate from `useClienteAcessoPortal` on purpose: that composable is about
 * the customer's ACCOUNT (does it exist, is it activated, what is the code).
 * This one is about telling them something, which is a different lifecycle and
 * a different failure surface.
 *
 * ⚠️ SUCCESS MEANS "IT IS IN THEIR INBOX", NOT "IT REACHED THEIR PHONE".
 * The portal stores the inbox row and answers 202, then attempts Web Push
 * asynchronously. `pushTargets === 0` is the ordinary case for a customer who
 * never enabled notifications — the message still waits for them in the app.
 * Every string this composable feeds the UI has to keep that distinction, or
 * attendants will believe a phone buzzed when nothing did.
 *
 * Per-caller instance (one open modal = one cliente).
 */

export interface EnviarNotificacaoResult {
  notificationId: string | null;
  /** Devices the push will be attempted against. Zero is normal, not a failure. */
  pushTargets: number | null;
  /**
   * `no_portal_account` — accepted and stored for a customer with no login yet;
   * it appears the moment one is provisioned. Worth surfacing, because staff can
   * create the access right there instead of assuming the message landed.
   * `no_devices` — there is a login, but no device subscribed.
   */
  warning: string | null;
}

export interface NotificacaoFailure {
  status: number;
  code: string;
  detail: string;
}

const ENVELOPE_ERROR_RE = /^\s*([a-z0-9_]+)\s*:\s*(.+)$/is;

function parseFailure(err: unknown): NotificacaoFailure {
  const httpErr = err as HttpError;
  const envelope = httpErr?.data as { error?: unknown } | undefined;
  const raw = typeof envelope?.error === 'string' ? envelope.error : '';
  const match = ENVELOPE_ERROR_RE.exec(raw);

  return {
    status: httpErr?.status ?? 0,
    code: match ? match[1] : '',
    detail: match ? match[2].trim() : raw.trim(),
  };
}

export function useClienteNotificacao() {
  const isSending = ref(false);
  const failure = ref<NotificacaoFailure | null>(null);
  const result = ref<EnviarNotificacaoResult | null>(null);

  /**
   * POST the message. The HTML goes over as-is; the SERVER flattens it to plain
   * text before storing (Portal_notification_php_service::plainTextFromHtml).
   * Doing it here as well would be a preview convenience, never the guarantee —
   * the body is rendered back to a customer, so the strip has to happen where it
   * cannot be bypassed.
   *
   * Never re-throws: the modal owns the feedback, and an unhandled rejection
   * would leave the button spinning with nothing on screen.
   */
  async function enviar(clienteId: number, mensagemHtml: string): Promise<boolean> {
    if (!clienteId || isSending.value) return false;

    isSending.value = true;
    failure.value = null;
    result.value = null;

    const body: Clientes_EnviarNotificacaoInput = {
      cliente_id: clienteId,
      mensagem: mensagemHtml,
    };

    try {
      const resp = await httpService.post<Clientes_EnviarNotificacaoResponse>(`/index.php/clientes/enviar-notificacao`, body, {
        inputContract: 'Clientes_EnviarNotificacaoInput',
        responseContract: 'Clientes_EnviarNotificacaoResponse',
        skipErrorDisplay: true,
      });

      const dados = resp.dados;
      result.value = {
        notificationId: dados?.notification_id ?? null,
        pushTargets: dados?.push_targets ?? null,
        warning: dados?.warning ?? null,
      };

      // The message body is NOT logged: it is the customer's personal data and
      // the debug log is far more widely read than their inbox.
      log('useClienteNotificacao', 'enviar', 'Notificação aceita pelo portal', {
        clienteId,
        pushTargets: result.value.pushTargets,
        warning: result.value.warning,
      });

      return true;
    } catch (err) {
      failure.value = parseFailure(err);
      log('useClienteNotificacao', 'enviar', 'Falha ao enviar a notificação', {
        clienteId,
        status: failure.value.status,
        code: failure.value.code,
      });
      return false;
    } finally {
      isSending.value = false;
    }
  }

  function reset(): void {
    isSending.value = false;
    failure.value = null;
    result.value = null;
  }

  // ── Histórico ─────────────────────────────────────────────────────────────
  const historicoRows = ref<Clientes_NotificacaoPortalRow[]>([]);
  const historicoTotal = ref(0);
  const historicoPage = ref(1);
  const historicoPerPage = ref(20);
  const historicoIsLoading = ref(false);
  const historicoFailed = ref(false);

  /**
   * Newest-first page of what was already sent to this customer. Reads the
   * LOCAL table (a row exists only when the portal accepted the message) —
   * no portal call, so it works even when the portal is unreachable.
   */
  async function carregarHistorico(clienteId: number, page = 1): Promise<void> {
    if (!clienteId) return;
    historicoIsLoading.value = true;
    historicoFailed.value = false;
    try {
      const resp = await httpService.get<Clientes_NotificacoesPortalResponse>(
        `/index.php/clientes/notificacoes-portal/${clienteId}?page=${page}&per_page=${historicoPerPage.value}`,
        { responseContract: 'Clientes_NotificacoesPortalResponse', skipErrorDisplay: true },
      );
      historicoRows.value = resp.dados?.rows ?? [];
      historicoTotal.value = resp.dados?.total ?? 0;
      historicoPage.value = resp.dados?.page ?? page;
    } catch (err) {
      // The history is secondary content inside the tab: a failure must not
      // block provisioning/sending, so it degrades to its own inline state.
      historicoFailed.value = true;
      historicoRows.value = [];
      historicoTotal.value = 0;
      log('useClienteNotificacao', 'carregarHistorico', 'Falha ao carregar o histórico', {
        clienteId,
        status: (err as HttpError | undefined)?.status,
      });
    } finally {
      historicoIsLoading.value = false;
    }
  }

  return {
    isSending, failure, result, enviar, reset,
    historicoRows, historicoTotal, historicoPage, historicoPerPage,
    historicoIsLoading, historicoFailed, carregarHistorico,
  };
}
